import { useCallback, useEffect, useState } from "react"
import { type Editor } from "@tiptap/react"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeTypeSelected,
} from "../../../lib/tiptap-utils"

// --- Icons ---
import { ImageCaptionIcon } from "../../../components/tiptap-icons/image-caption-icon"

/**
 * Configuration for the media caption functionality
 */
export interface UseMediaCaptionConfig {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null
  /**
   * Whether the button should hide when caption toggle is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean
  /**
   * Callback function called after a successful caption set.
   */
  onSet?: () => void
}

/**
 * Checks if media caption can be toggled in the current editor state
 */
export function canToggleMediaCaption(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, ["image", "video", "embedVideo"])) return false

  return isNodeTypeSelected(editor, ["image", "video", "embedVideo"])
}

/**
 * Toggles the media caption in the editor
 */
export function setMediaCaption(editor: Editor | null): boolean {
  if (!editor?.isEditable || !canToggleMediaCaption(editor)) {
    return false
  }

  try {
    const { selection } = editor.state
    const isMediaSelected =
      selection instanceof NodeSelection && 
      (selection.node.type.name === "image" || selection.node.type.name === "video" || selection.node.type.name === "embedVideo")

    if (!isMediaSelected) {
      return false
    }

    const nodeType = selection.node.type.name
    const captionEnabled = editor
      .chain()
      .focus()
      .updateAttributes(nodeType, { showCaption: true })
      .run()

    if (!captionEnabled) {
      return false
    }

    const mediaPosition = selection.from
    editor
      .chain()
      .focus(mediaPosition + 1)
      .selectTextblockEnd()
      .run()

    return true
  } catch {
    return false
  }
}

/**
 * Determines if the media caption button should be shown
 */
export function shouldShowButton(props: {
  editor: Editor | null
  hideWhenUnavailable: boolean
}): boolean {
  const { editor, hideWhenUnavailable } = props

  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, ["image", "video", "embedVideo"])) return false

  if (hideWhenUnavailable) {
    return canToggleMediaCaption(editor)
  }

  return true
}

/**
 * Custom hook that provides media caption functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyMediaCaptionButton() {
 *   const { isVisible, handleToggleCaption } = useMediaCaption()
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleToggleCaption}>Toggle Caption</button>
 * }
 *
 * // Advanced usage
 * function MyAdvancedMediaCaptionButton() {
 *   const { isVisible, handleToggleCaption, label, canToggle } = useMediaCaption({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onSet: () => console.log('Caption set!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggleCaption}
 *       disabled={!canToggle}
 *       aria-label={label}
 *     >
 *       Toggle Caption
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useMediaCaption(config?: UseMediaCaptionConfig) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onSet,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canToggle = canToggleMediaCaption(editor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    }
  }, [editor, hideWhenUnavailable])

  const handleToggleCaption = useCallback(() => {
    if (!editor) return false

    const success = setMediaCaption(editor)
    if (success) {
      onSet?.()
    }
    return success
  }, [editor, onSet])

  return {
    isVisible,
    canToggle,
    handleToggleCaption,
    label: "Caption",
    Icon: ImageCaptionIcon,
  }
}

// Backward compatibility aliases
export type UseImageCaptionConfig = UseMediaCaptionConfig
export const canToggleImageCaption = canToggleMediaCaption
export const setImageCaption = setMediaCaption
export const useImageCaption = useMediaCaption
